import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessions, getSessionEvents, getSyncEvent } from '../api';
import { formatTimestamp, formatRelativeTime, getEventTypeIcon } from '../utils';
import { useFilters } from '../hooks';
import type { SessionSummary, SyncEvent, SyncEventFilters, FileUploadRequestSummary } from '../types';
import styles from './Sessions.module.css';

/**
 * Format raw bytes diagnostic for copy
 */
function formatRawBytesDiagnostic(event: SyncEvent): string {
  const uploadRequest = event.request_summary as FileUploadRequestSummary | undefined;
  const diagnostic = uploadRequest?.upload_validation?.raw_bytes_diagnostic;
  
  if (!diagnostic) return '';
  
  return `
RAW BYTES DIAGNOSTIC
--------------------
Null Byte Count: ${diagnostic.null_byte_count.toLocaleString()}
Null Percentage: ${diagnostic.null_percentage.toFixed(1)}%
First 200 (readable): ${diagnostic.first_200_readable || '(empty)'}
Last 200 (readable): ${diagnostic.last_200_readable || '(empty)'}
`;
}

/**
 * Sessions Page
 * 
 * View sync sessions - grouped events from single sync cycles.
 * Uses the backend /sessions/ endpoint for proper grouping.
 */

export default function Sessions() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters - timeRange is shared across tabs
  const { timeRange, setTimeRange } = useFilters();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 30;

  // Expanded session details
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [sessionEvents, setSessionEvents] = useState<SyncEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [copiedSession, setCopiedSession] = useState(false);

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const filters: Pick<SyncEventFilters, 'user_email' | 'device_code' | 'timestamp_gte' | 'timestamp_lte' | 'page' | 'page_size'> = {
        page,
        page_size: pageSize,
      };

      // Apply search filter
      if (search.trim()) {
        if (search.includes('@')) {
          filters.user_email = search.trim();
        } else {
          filters.device_code = search.trim();
        }
      }

      // Apply time range filter
      if (timeRange) {
        const now = new Date();
        let since: Date;
        switch (timeRange) {
          case '1h':
            since = new Date(now.getTime() - 60 * 60 * 1000);
            break;
          case '24h':
            since = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case '7d':
            since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30d':
            since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            since = new Date(0);
        }
        filters.timestamp_gte = since.toISOString();
      }

      const response = await getSessions(filters);
      setSessions(response.results || []);
      setTotalCount(response.count);
    } catch (err) {
      console.error('[Sessions] Error fetching:', err);
      setError('Failed to load sessions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [search, timeRange, page]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, timeRange]);

  // Fetch session events when expanded
  const handleExpandSession = async (sessionId: string) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
      setSessionEvents([]);
      return;
    }

    setExpandedSessionId(sessionId);
    setLoadingEvents(true);

    try {
      const events = await getSessionEvents(sessionId);
      // Handle both array and object responses
      const eventList = Array.isArray(events) ? events : (events as any).events || [];
      setSessionEvents(eventList.sort((a: SyncEvent, b: SyncEvent) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      ));
    } catch (err) {
      console.error('[Sessions] Error fetching session events:', err);
      setSessionEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Copy entire session to clipboard
  const [isCopyingSession, setIsCopyingSession] = useState(false);
  
  const copySessionToClipboard = async (session: SessionSummary, events: SyncEvent[]) => {
    setIsCopyingSession(true);
    
    try {
      // Fetch full details for each event
      const fullEvents = await Promise.all(
        events.map(e => getSyncEvent(e.id).catch(() => e))
      );
      
      const sortedEvents = [...fullEvents].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      const sessionText = `SESSION INFO
============
Session ID: ${session.session_id}
Timestamp: ${session.timestamp}
User Email: ${session.user_email || '-'}
Device Code: ${session.device_code || '-'}
Device Name: ${session.device_name || '-'}
Total Events: ${session.event_count}
Uploads: ${session.upload_count}
Downloads: ${session.download_count}
Has Corruption: ${session.has_corruption ? 'Yes' : 'No'}
Has Errors: ${session.has_errors ? 'Yes' : 'No'}

${'='.repeat(80)}
SESSION EVENTS
${'='.repeat(80)}

${sortedEvents.map((e, idx) => {
  const quarantineSection = e.was_quarantined 
    ? `
🔒 QUARANTINE STATUS
--------------------
Was Quarantined: Yes
Reason: ${e.quarantine_reason || 'Unknown'}
` : '';

  const corruptionSection = e.has_suspicious_chars
    ? `
⚠️ CORRUPTION DETECTED
----------------------
Suspicious Characters: ${e.suspicious_char_count?.toLocaleString() || 'Unknown'}
Details: ${e.suspicious_details || 'No details'}
` : '';

  return `
${'─'.repeat(80)}
EVENT ${idx + 1} OF ${sortedEvents.length}
${'─'.repeat(80)}

EVENT INFO
----------
Event ID: ${e.id}
Event Type: ${e.event_type}
Timestamp: ${e.timestamp}
Firmware Version: ${e.firmware_version || '-'}
File Name: ${e.file_name || '-'}
File ID: ${e.file_id || '-'}
Project: ${e.project_name || '-'}
Status: ${e.success ? 'Success' : 'Failed'}
${quarantineSection}${corruptionSection}${formatRawBytesDiagnostic(e)}
ENDPOINT
--------
${e.endpoint || 'Not recorded'}

REQUEST SUMMARY
---------------
${e.request_summary ? JSON.stringify(e.request_summary, null, 2) : '(No request summary)'}

RESPONSE SUMMARY
----------------
${e.response_summary ? JSON.stringify(e.response_summary, null, 2) : '(No response summary)'}

RAW CONTENT (FROM DEVICE)
-------------------------
${e.content_raw || '(No raw content)'}

PROCESSED CONTENT (AFTER SERVER PROCESSING)
--------------------------------------------
${e.content_processed || '(No processed content)'}
`;
}).join('\n')}`;

      await navigator.clipboard.writeText(sessionText);
      setCopiedSession(true);
      setTimeout(() => setCopiedSession(false), 2000);
    } catch (err) {
      console.error('Failed to copy session:', err);
    } finally {
      setIsCopyingSession(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Sync Sessions</h1>
        <p className={styles.subtitle}>
          {totalCount.toLocaleString()} sessions found. Each session groups related handshakes, uploads, and downloads.
        </p>
      </div>

      <div className={styles.filters}>
        <input
          type="text"
          placeholder="Search by user email or device code..."
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={styles.filterGroup}>
          <select 
            className={styles.select}
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="">All Time</option>
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {isLoading ? (
        <div className={styles.loading}>Loading sessions...</div>
      ) : sessions.length === 0 ? (
        <div className={styles.placeholder}>
          <p>No sessions found matching your filters.</p>
        </div>
      ) : (
        <div className={styles.sessionList}>
          {sessions.map((session) => (
            <div key={session.session_id} className={styles.sessionCard}>
              <div 
                className={styles.sessionHeader}
                onClick={() => handleExpandSession(session.session_id)}
              >
                <div className={styles.sessionMain}>
                  <div className={styles.sessionTime}>
                    <span className={styles.sessionTimestamp}>{formatTimestamp(session.timestamp)}</span>
                    <span className={styles.sessionRelative}>{formatRelativeTime(session.timestamp)}</span>
                  </div>
                  <div className={styles.sessionInfo}>
                    <span className={styles.sessionUser}>{session.user_email || 'Unknown user'}</span>
                    <span className={styles.separator}>•</span>
                    <span className={styles.sessionDevice}>
                      <span className={styles.deviceCode}>{session.device_code || '?'}</span>
                      {session.device_name && <span className={styles.deviceName}>({session.device_name})</span>}
                    </span>
                  </div>
                </div>
                <div className={styles.sessionStats}>
                  <div className={styles.statItem}>
                    <span className={styles.statValue}>{session.event_count}</span>
                    <span className={styles.statLabel}>events</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statValue}>{session.upload_count}</span>
                    <span className={styles.statLabel}>uploads</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statValue}>{session.download_count}</span>
                    <span className={styles.statLabel}>downloads</span>
                  </div>
                  {session.has_corruption && (
                    <span className={styles.corruptionBadge}>⚠️ Corrupted</span>
                  )}
                  {session.has_errors && (
                    <span className={styles.errorBadge}>❌ Errors</span>
                  )}
                </div>
                <span className={styles.expandIcon}>
                  {expandedSessionId === session.session_id ? '▼' : '▶'}
                </span>
              </div>

              {expandedSessionId === session.session_id && (
                <div className={styles.sessionExpanded}>
                  <div className={styles.sessionIdRow}>
                    <span className={styles.sessionIdLabel}>Session ID:</span>
                    <code className={styles.sessionIdValue}>{session.session_id}</code>
                    {sessionEvents.length > 0 && (
                      <button
                        className={styles.copySessionButton}
                        disabled={isCopyingSession}
                        onClick={(e) => {
                          e.stopPropagation();
                          copySessionToClipboard(session, sessionEvents);
                        }}
                      >
                        {isCopyingSession ? '⏳ Loading...' : copiedSession ? '✓ Copied!' : '📋 Copy Entire Session'}
                      </button>
                    )}
                  </div>
                  
                  {loadingEvents ? (
                    <div className={styles.loadingEvents}>Loading events...</div>
                  ) : sessionEvents.length === 0 ? (
                    <div className={styles.noEvents}>No events found for this session.</div>
                  ) : (
                    <div className={styles.eventTimeline}>
                      {sessionEvents.map((event, idx) => (
                        <div 
                          key={event.id} 
                          className={styles.timelineEvent}
                          onClick={() => navigate(`/events/${event.id}`)}
                        >
                          <div className={styles.timelineMarker}>
                            <span className={styles.timelineNumber}>{idx + 1}</span>
                          </div>
                          <div className={styles.timelineContent}>
                            <div className={styles.timelineHeader}>
                              <span className={styles.timelineIcon}>{getEventTypeIcon(event.event_type)}</span>
                              <span className={styles.timelineType}>{event.event_type}</span>
                              {event.file_name && (
                                <span className={styles.timelineFile}>— {event.file_name}</span>
                              )}
                              {event.has_suspicious_chars && <span className={styles.timelineCorrupted}>⚠️</span>}
                              {!event.success && <span className={styles.timelineFailed}>❌</span>}
                            </div>
                            <div className={styles.timelineMeta}>
                              {formatTimestamp(event.timestamp)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ← Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <button
            className={styles.pageButton}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
